import { accountDraftOf, Integration, IntegrationAccountDraft, LocalEditableIntegration, KrossBookingProperty, IntegrationCompany, HostifyListing, SmoobuApartment, OctorateAccommodation, AvaibookAccommodation, LodgifyProperty, GuestyListing } from "../../../Everou/_modelsTS/Integration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Selector, CapsuleButton } from "../../../Everou/_components"
import { Icons } from "../../../Everou/_assets";
import { integrationLogoForCompany } from "../../../Everou/_assets/integrationsLogo";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { PMSKrossBookingStore } from "../../_stores/PMSKrossBookingStore";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { PMSIntegrationCreateServer } from "../../_models/PMSIntegration";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { IntegrationSyncStatus } from "../../ModuleIntegrations/_components/IntegrationSyncStatus";
import { PMSHostifyStore } from "../../_stores/PMSHostifyStore";
import { PMSSmoobuStore } from "../../_stores/PMSSmoobuStore";
import { PMSOctorateStore } from "../../_stores/PMSOctorateStore";
import { WaitSpinner } from "../../../Common/_components/WaitSpinner";
import { PMSAvaibookStore } from "../../_stores/PMSAvaibookStore";
import { PMSLodgifyStore } from "../../_stores/PMSLodgifyStore";
import { URL_CONSTANTS } from "../../../Everou/_constants";
import { TimeHelper } from "../../../Everou/_helpers/TimeHelper";
import { PMSGuestyStore } from "../../_stores/PMSGuestyStore";
import { PMSAvantioStore } from "../../_stores/PMSAvantioStore";
import ToolTipWrapper from "../../../Everou/_components/ToolTipWrapper";
import { ValidatedTextInput } from "../../../Common/_components/ValidatedTextInput";
import { integrationTemplateLabel } from "../../_helpers/IntegrationTemplateHelper";
import { IntegrationTemplateRef, PMSNavigationActions } from "../../_actions/PMSNavigationActions";
import { VACATION_RENTAL_PLANS } from "../../_constants/vacationRentalPlan.constants";
import { useError } from "../../../Everou/_hooks/useError";


// External-app / developers integrations: the backend stamps
// last_reservations_checked on them too, but they don't pull reservations, so
// the value is meaningless. Hide the "last sync" line for these to avoid
// presenting a misleading time. Lodgify and Enso Connect are NOT here — once
// they report real syncs the line shows up on its own. Guesty is excluded
// pending confirmation that its value reflects a real sync.
const COMPANIES_WITHOUT_RESERVATION_SYNC: IntegrationCompany[] = [
    IntegrationCompany.THREE_ANGULAR,
    IntegrationCompany.BOOKIPRO,
    IntegrationCompany.ICNEA,
    IntegrationCompany.GUESTY,
    // Manual has nothing to sync with: its reservations are typed in here.
    IntegrationCompany.MANUAL,
];

type IntegrationConfigInputProps = ({
    integration: LocalEditableIntegration;
    integrationTemplates: any[];
    // Whether integrationTemplates is the viewer's actual list yet. Loading
    // (or failed), it is empty, and an empty list reads as "none of these
    // accounts is yours": everything would look foreign and every orphan
    // would offer to create an account the viewer may already have.
    templatesLoaded?: boolean;
    groups: any[]; // To be typed asap
    onDelete: (index: number) => void;
    index: number;
    initialTemplate?: Integration;
    // Called once the row this form was adding exists on the server, so the
    // owner of the list can drop the local one.
    onCreated?: () => void;
    // Where the account is looked at in place (a location outside any
    // organization: its personal accounts have no screen of their own) instead
    // of navigating to the organization's tab.
    onBrowseAccount?: (
        company: IntegrationCompany,
        template: IntegrationTemplateRef | null,
        draft?: IntegrationAccountDraft | null,
    ) => void;
})

type IntegrationTemplateOption = {
    value: string;
    label: IntegrationCompany;
    displayLabel?: string;
    isDisabled: boolean;
};

export function IntegrationConfigInput({
    integration,
    integrationTemplates,
    templatesLoaded = true,
    index,
    groups,
    onDelete,
    initialTemplate,
    onBrowseAccount,
    onCreated,
}: IntegrationConfigInputProps) {

    // LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // GLOBAL STATE
    const location = useSelector(Selectors.getPMSLocationInfo);

    const locationRooms = useSelector(Selectors.getPMSLocationRooms);
    const buildAvailableLocationRooms = useCallback(
        (availableLocationRooms: any[] = []) => {
            return availableLocationRooms.map(
                (room) => (
                    { 
                        value: room.uid, 
                        label: room.name, 
                        isDisabled: false
                    }
                )
            );
    }, []);
    const [availableLocationRooms, setAvailableLocationRooms] = useState(buildAvailableLocationRooms(locationRooms));
    const [selectedLocationRoomsLocal, setSelectedLocationRoomsLocal] = useState<any[]>([]);

    // How a template READS. Shared by the picker, the header of the form it
    // opens and the one-shot initialization, which each used to build it
    // themselves: the moment the picker started naming templates, the other two
    // kept saying the bare company, so the same screen named the same template
    // two different ways depending on how it had been chosen.
    const templateDisplayLabel = useCallback((template: any) => (
        template.isPersonal
            ? `${integrationTemplateLabel(template)} (${t("mod_pms_integration_template_personal")})`
            : integrationTemplateLabel(template)
    ), [t]);

    const buildAvailablePMS = useCallback((availableIntegrationTemplates: any[] = []): IntegrationTemplateOption[] => {
        return availableIntegrationTemplates.map(
            (template) => (
                {
                    value: template.uid,
                    // The list merges the organization's templates with the
                    // personal (legacy) ones, so two of the same company would
                    // otherwise be offered as two identical, indistinguishable
                    // options. Keep `label` as the canonical company value:
                    // the rest of the form uses it to select the platform
                    // branch. Only decorate the text rendered by the picker.
                    label: template.company,
                    displayLabel: templateDisplayLabel(template),
                    isDisabled: false
                }
            )
        ) ?? [];
    }, [templateDisplayLabel]);

    const initialTemplateOption: IntegrationTemplateOption | undefined =
        initialTemplate ? {
            value: initialTemplate.uid,
            label: initialTemplate.company,
            displayLabel: templateDisplayLabel(initialTemplate),
            isDisabled: false,
        } : integration.company === IntegrationCompany.GENERIC ? undefined : {
            value: integration.uid,
            label: integration.company,
            isDisabled: false,
        };
    const [selectedPMSLocal, setSelectedPMSLocal] = useState<IntegrationTemplateOption | undefined>(
        initialTemplateOption,
    );
    useEffect(() => {
        // Only for a SAVED row, which is described by its own integration. The
        // row being added is described by the template it was opened with (and
        // by the picker): it is GENERIC, so re-deriving from it would blank a
        // choice already made -- every refresh of the list handing down a new
        // object was enough to do it.
        if (initialTemplate) return;

        setSelectedPMSLocal(
            integration.company === IntegrationCompany.GENERIC ? undefined : 
            { 
                value:integration.uid, 
                label:integration.company, 
                isDisabled: false 
            }
        );
    }, [integration, initialTemplate]);
    const [availablePMS, setAvailablePMS] = useState(buildAvailablePMS(integrationTemplates));

    const [nextPropertiesPage, setNextPropertiesPage] = useState(1);
    const [isLoadingAvantioAccommodations, setIsLoadingAvantioAccommodations] = useState(false);

    const onChangePMS = useCallback((selectedPMS: IntegrationTemplateOption) => {
        setSelectedPMSLocal(selectedPMS);
        if (selectedPMS.label === IntegrationCompany.KROSS_BOOKING) {
            dispatch(PMSIntegrationsActions.listKrossBookingProperties(selectedPMS.value));

        } else if (selectedPMS.label === IntegrationCompany.HOSTIFY) {
            dispatch(PMSIntegrationsActions.listHostifyListings(selectedPMS.value, nextPropertiesPage));
            setNextPropertiesPage(nextPropertiesPage+1);

        } else if (selectedPMS.label === IntegrationCompany.SMOOBU) {
            dispatch(PMSIntegrationsActions.listSmoobuProperties(selectedPMS.value));
        
        } else if (selectedPMS.label === IntegrationCompany.OCTORATE) {
            dispatch(PMSIntegrationsActions.listOctorateAccommodations(selectedPMS.value));

        } else if (selectedPMS.label === IntegrationCompany.AVAIBOOK) {
            dispatch(PMSIntegrationsActions.listAvaibookAccommodations(selectedPMS.value));

        } else if (selectedPMS.label === IntegrationCompany.LODGIFY) {
            dispatch(PMSIntegrationsActions.listLodgifyProperties(selectedPMS.value));

        } else if (selectedPMS.label === IntegrationCompany.GUESTY) {
            dispatch(PMSIntegrationsActions.listGuestyListings(selectedPMS.value));

        } else if (selectedPMS.label === IntegrationCompany.AVANTIO) {
            setIsLoadingAvantioAccommodations(true);
            Promise.resolve(
                dispatch(PMSIntegrationsActions.listAvantioAccommodations(selectedPMS.value)),
            ).finally(() => setIsLoadingAvantioAccommodations(false));

        }

    }, [dispatch, nextPropertiesPage, setNextPropertiesPage]);

    // The template is now chosen by the page-level "Add integration" action.
    // Initialize this form with it and run the platform-specific property load.
    useEffect(() => {
        if (!initialTemplate) return;
        onChangePMS({
            value: initialTemplate.uid,
            label: initialTemplate.company,
            displayLabel: templateDisplayLabel(initialTemplate),
            isDisabled: false,
        });
        // The template uid identifies this one-shot form initialization.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTemplate?.uid]);

    // If there is only one available PMS, select it automatically so the
    // user goes straight to its configuration instead of picking from a
    // single-option dropdown.
    useEffect(() => {
        if (
            integration.company === IntegrationCompany.GENERIC &&
            availablePMS.length === 1 &&
            !selectedPMSLocal
        ) {
            onChangePMS(availablePMS[0]);
        }
    }, [integration.company, availablePMS, selectedPMSLocal, onChangePMS]);

    useEffect(() => {
        setAvailableLocationRooms(buildAvailableLocationRooms(locationRooms));
    }, [locationRooms]);

    // region KROSS BOOKING
    const buildKrossBookingPropertyOptions = useCallback((availableKrossBookingProperties: any[] = []) => {
        return availableKrossBookingProperties.map((property) => ({ value: property.id_property, label: property.name, isDisabled: false })) ?? [];
    }, []);

    const buildKrossBookingRoomOptions = useCallback((availableKrossBookingRooms: any[] = []) => {
        return availableKrossBookingRooms.map((room) => ({ value: room.id_room, label: room.number, isDisabled: false })) ?? [];
    }, []);

    const krossBookingProperties = useSelector(PMSKrossBookingStore.selectorGetItems);
    const selectedKrossBookingProperty : KrossBookingProperty = useSelector(PMSKrossBookingStore.selectorGetSelectedItem) ?? {
        id:integration.host_id, 
        name:integration.host_name
    };
    const [selectedKrossBookingPropertyLocal, setSelectedKrossBookingPropertyLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableKrossBookingProperties, setAvailableKrossBookingProperties] = useState(buildKrossBookingPropertyOptions(krossBookingProperties));

    const onChangeKrossBookingProperty = useCallback(selectedKrossBookingProperty => {
        setSelectedKrossBookingPropertyLocal(selectedKrossBookingProperty);
        dispatch(PMSKrossBookingStore.actionSelectItem(selectedKrossBookingProperty.value));
        if (selectedPMSLocal && selectedKrossBookingProperty !== undefined) {
            dispatch(PMSIntegrationsActions.listKrossBookingRooms(
                selectedPMSLocal.value, 
                selectedKrossBookingProperty.value
            ));
        }
    }, [dispatch, selectedPMSLocal]);

    const [selectedKrossBookingRoomLocal, setSelectedKrossBookingRoomLocal] = useState(
        { 
            value:integration.room_id, 
            label:integration.room_name, 
            isDisabled: false 
        }
    );
    const [availableKrossBookingRooms, setAvailableKrossBookingRooms] = useState(buildKrossBookingRoomOptions(selectedKrossBookingProperty?.rooms));

    const onChangeKrossBookingRoom = useCallback(selectedRoom => {
        setSelectedKrossBookingRoomLocal(selectedRoom);
    }, []);

    useEffect(() => {
        setAvailableKrossBookingProperties(buildKrossBookingPropertyOptions(krossBookingProperties));
    }, [krossBookingProperties, buildKrossBookingPropertyOptions]);

    useEffect(() => {
        if (!!selectedKrossBookingProperty && !!selectedKrossBookingProperty.rooms) {
            setAvailableKrossBookingRooms(buildKrossBookingRoomOptions(selectedKrossBookingProperty.rooms));
        }
    }, [buildKrossBookingRoomOptions, selectedKrossBookingProperty]);
    // endregion

    // region HOSTIFY
    const buildHostifyPropertyOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map(
            (property) => (
                { 
                    value: property.id, 
                    label: property.nickname, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);

    const hostifyProperties = useSelector(PMSHostifyStore.selectorGetItems);
    const selectedHostifyProperty : HostifyListing = useSelector(PMSHostifyStore.selectorGetSelectedItem);
    
    const [selectedHostifyPropertyLocal, setSelectedHostifyPropertyLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableHostifyProperties, setAvailableHostifyProperties] = useState(buildHostifyPropertyOptions(hostifyProperties));

    const onChangeHostifyProperty = useCallback(selectedProperty => {
        setSelectedHostifyPropertyLocal(selectedProperty);
        dispatch(PMSHostifyStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    function onLoadMoreHostifyProperties() {
        if (selectedPMSLocal !== undefined) {
            dispatch(PMSIntegrationsActions.listHostifyListings(selectedPMSLocal.value, nextPropertiesPage));
            setNextPropertiesPage(nextPropertiesPage+1);
        }
    }

    useEffect(() => {
        const loadedProperties = buildHostifyPropertyOptions(hostifyProperties);
        const merged = [...availableHostifyProperties, ...loadedProperties];
        setAvailableHostifyProperties(merged);
    }, [hostifyProperties, buildHostifyPropertyOptions]);
    // endregion

    // region SMOOBU
    const buildSmoobuPropertyOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map(
            (property) => (
                { 
                    value: property.id, 
                    label: property.name, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);

    const smoobuProperties = useSelector(PMSSmoobuStore.selectorGetItems);
    const selectedProperty : SmoobuApartment = useSelector(PMSSmoobuStore.selectorGetSelectedItem) ?? {
        id:integration.host_id, 
        name:integration.host_name
    };
    const [selectedSmoobuPropertyLocal, setSelectedSmoobuPropertyLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableSmoobuProperties, setAvailableSmoobuProperties] = useState(buildSmoobuPropertyOptions(smoobuProperties));

    const onChangeSmoobuProperty = useCallback(selectedProperty => {
        setSelectedSmoobuPropertyLocal(selectedProperty);
        dispatch(PMSSmoobuStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    useEffect(() => {
        setAvailableSmoobuProperties(buildSmoobuPropertyOptions(smoobuProperties));
    }, [smoobuProperties, buildSmoobuPropertyOptions]);
    // endregion

    // region iCAL
    const [selectedIcalLinkLocal, setSelectedIcalLinkLocal] = useState(
        { 
            value:integration.uid, 
            label:integration.link, 
            isDisabled: false 
        }
    );

    const onChangeIcalLink = useCallback(selectedIcalLink => {
        setSelectedIcalLinkLocal(selectedIcalLink);
    }, []);

    const buildAvailableIcalLinks = useCallback((availableIntegrationTemplates: any[] = []) => {
        return availableIntegrationTemplates.map(
            (template) => (
                { 
                    value: template.uid, 
                    label: template.company, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);
    // endregion

    // region AVANTIO
    const buildAvantioAccommodationOptions = useCallback((availableAccommodations: any[] = []) => {
        return availableAccommodations.map(
            (accommodation) => (
                {
                    value: accommodation.id,
                    label: accommodation.name,
                    isDisabled: false,
                }
            )
        ) ?? [];
    }, []);

    const avantioAccommodations = useSelector(PMSAvantioStore.selectorGetItems);

    const [selectedAvantioAccommodationLocal, setSelectedAvantioAccommodationLocal] = useState(
        {
            value: integration.host_id,
            label: integration.host_name,
            isDisabled: false,
        }
    );
    const [availableAvantioAccommodations, setAvailableAvantioAccommodations] = useState(buildAvantioAccommodationOptions(avantioAccommodations));

    const onChangeAvantioAccommodation = useCallback(selectedProperty => {
        setSelectedAvantioAccommodationLocal(selectedProperty);
        dispatch(PMSAvantioStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    useEffect(() => {
        setAvailableAvantioAccommodations(buildAvantioAccommodationOptions(avantioAccommodations));
    }, [avantioAccommodations, buildAvantioAccommodationOptions]);
    // endregion

    // region AVAIBOOK

    // region old
    const [availableAvaibookIcalLinks, setAvailableAvaibookIcalLinks] = useState(buildAvailableIcalLinks(integrationTemplates));

    const buildAvailableAvaibookIcalLinks = useCallback((availableTemplates: any[] = []) => {
        return availableTemplates.filter(template => template.company === IntegrationCompany.AVAIBOOK).map(
            (template) => (
                { 
                    value: template.uid, 
                    label: template.link, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);

    useEffect(() => {
        setAvailableAvaibookIcalLinks(buildAvailableAvaibookIcalLinks(integrationTemplates));
    }, [integrationTemplates, buildAvailableAvaibookIcalLinks]);
    // endregion

    // region new
    const buildAvaibookAccommodationsOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map(
            (property) => (
                { 
                    value: property.id, 
                    label: property.name, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);

    const avaibookAccommodations = useSelector(PMSAvaibookStore.selectorGetItems);
    const selectedAvaibookAccommodation : AvaibookAccommodation = useSelector(PMSAvaibookStore.selectorGetSelectedItem);

    const [selectedAvaibookAccommodationLocal, setSelectedAvaibookAccommodationLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableAvaibookAccommodations, setAvailableAvaibookAccommodations] = useState(buildAvaibookAccommodationsOptions(avaibookAccommodations));

    const onChangeAvaibookAccommodations = useCallback(selectedProperty => {
        setSelectedAvaibookAccommodationLocal(selectedProperty);
        dispatch(PMSAvaibookStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    useEffect(() => {
        setAvailableAvaibookAccommodations(buildAvaibookAccommodationsOptions(avaibookAccommodations));
    }, [avaibookAccommodations, buildAvaibookAccommodationsOptions]);
    // endregion

    // endregion

    // region OCTORATE
    const buildOctorateAccomodationOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map((property) => ({ value: property.id, label: property.name, isDisabled: false })) ?? [];
    }, []);

    const octorateAccomodations = useSelector(PMSOctorateStore.selectorGetItems);
    const selectedOctorateAccomodation : OctorateAccommodation = useSelector(PMSOctorateStore.selectorGetSelectedItem) ?? {id:integration.host_id, name:integration.host_name};
    const [selectedOctorateAccomodationLocal, setSelectedOctorateAccomodationLocal] = useState({ 
        value:integration.host_id, 
        label:integration.host_name, 
        isDisabled: 
        false 
    });
    const [availableOctorateAccomodations, setAvailableOctorateAccomodations] = useState(buildOctorateAccomodationOptions(octorateAccomodations));

    const onChangeOctorateAccomodation = useCallback(selectedProperty => {
        setSelectedOctorateAccomodationLocal(selectedProperty);
        dispatch(PMSOctorateStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    useEffect(() => {
        setAvailableOctorateAccomodations(buildOctorateAccomodationOptions(octorateAccomodations));
    }, [octorateAccomodations, buildOctorateAccomodationOptions]);
    // endregion

    // region LODGIFY
    const buildLodgifyPropertyOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map(
            (property) => (
                { 
                    value: property.id, 
                    label: property.name, 
                    isDisabled: false 
                }
            )
        ) ?? [];
    }, []);

    const lodgifyProperties = useSelector(PMSLodgifyStore.selectorGetItems);
    const selectedLodgifyProperty : LodgifyProperty = useSelector(PMSLodgifyStore.selectorGetSelectedItem);

    const [selectedLodgifyPropertyLocal, setSelectedLodgifyPropertyLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableLodgifyProperties, setAvailableLodgifyProperties] = useState(buildLodgifyPropertyOptions(lodgifyProperties));

    const onChangeLodgifyProperty = useCallback(selectedProperty => {
        setSelectedLodgifyPropertyLocal(selectedProperty);
        dispatch(PMSLodgifyStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    useEffect(() => {
        setAvailableLodgifyProperties(buildLodgifyPropertyOptions(lodgifyProperties));
    }, [lodgifyProperties, buildLodgifyPropertyOptions]);
    // endregion

    // region GUESTY
    const buildGuestyListingOptions = useCallback((availableGuestyListings: any[] = []) => {
        return availableGuestyListings.map((property) => ({ value: property._id, label: property.title, isDisabled: false })) ?? [];
    }, []);

    const guestyListings = useSelector(PMSGuestyStore.selectorGetItems);
    const selectedGuestyListing : GuestyListing = useSelector(PMSGuestyStore.selectorGetSelectedItem) ?? {
        _id:integration.host_id, 
        title:integration.host_name
    };
    const [selectedGuestyListingLocal, setSelectedGuestyListingLocal] = useState(
        { 
            value:integration.host_id, 
            label:integration.host_name, 
            isDisabled: false 
        }
    );
    const [availableGuestyListings, setAvailableGuestyListings] = useState(buildGuestyListingOptions(guestyListings));

    const onChangeGuestyListing = useCallback(selectedGuestyListing => {
        setSelectedGuestyListingLocal(selectedGuestyListing);
        dispatch(PMSGuestyStore.actionSelectItem(selectedGuestyListing.value));
    }, [dispatch, selectedPMSLocal]);

    useEffect(() => {
        setAvailableGuestyListings(buildGuestyListingOptions(guestyListings));
    }, [guestyListings, buildGuestyListingOptions]);
    // endregion

    // region ENSO CONNECT
    const [host_name, setName] = useState(integration.host_name);
    const [host_id, setId] = useState(integration.host_id?.toString());

    // endregion

    const onChangeRoomsGroup = useCallback(selectedRooms => {
        setSelectedLocationRoomsLocal(selectedRooms);
    }, []);

    const [isRequestingForSave, setRequestSave] = useState(false);
    const { errorMessage, setError, disableError } = useError();

    // Edit mode for an already-saved integration: drop back into the create form
    // pre-filled and persist through the location-integration update endpoint
    // (create stays keyed on the template uid, so it can't update in place).
    const [isEditing, setIsEditing] = useState(false);

    // Form fields are editable for brand-new integrations (integration.editable)
    // and while editing a saved one.
    const isEditable = integration.editable || isEditing;

    const onSave = useCallback(async () => {
        if (!selectedPMSLocal)
            return;

        let editedIntegration: PMSIntegrationCreateServer;
        
        if (selectedPMSLocal.label  === IntegrationCompany.KROSS_BOOKING) {
            if (!selectedKrossBookingPropertyLocal || !selectedKrossBookingRoomLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedKrossBookingPropertyLocal.value,
                host_name: selectedKrossBookingPropertyLocal.label,
                room_id: selectedKrossBookingRoomLocal.value,
                room_name: selectedKrossBookingRoomLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.HOSTIFY) {
            if (!selectedHostifyPropertyLocal)
                return;

            const url = URL_CONSTANTS.URL_API + URL_CONSTANTS.INTEGRATION_HOSTIFY_HANDLER;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedHostifyPropertyLocal.value,
                host_name: selectedHostifyPropertyLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
                handler_url: url
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.SMOOBU) {
            if (!selectedSmoobuPropertyLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedSmoobuPropertyLocal.value,
                host_name: selectedSmoobuPropertyLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.AVANTIO) {
            if (!selectedAvantioAccommodationLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedAvantioAccommodationLocal.value,
                host_name: selectedAvantioAccommodationLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.OCTORATE) {
            if (!selectedSmoobuPropertyLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedOctorateAccomodationLocal.value,
                host_name: selectedOctorateAccomodationLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.AVAIBOOK) {
            if (!selectedAvaibookAccommodationLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedAvaibookAccommodationLocal.value,
                host_name: selectedAvaibookAccommodationLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.LODGIFY) {
            if (!selectedLodgifyPropertyLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedLodgifyPropertyLocal.value,
                host_name: selectedLodgifyPropertyLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label  === IntegrationCompany.GUESTY) {
            if (!selectedGuestyListingLocal)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: selectedGuestyListingLocal.value,
                host_name: selectedGuestyListingLocal.label,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label  === IntegrationCompany.THREE_ANGULAR) {
            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: 0,
                host_name: 'None',
                rooms: availableLocationRooms.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label  === IntegrationCompany.ICNEA) {
            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: 0,
                host_name: 'None',
                rooms: availableLocationRooms.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label  === IntegrationCompany.BOOKIPRO) {
            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: 0,
                host_name: 'None',
                rooms: availableLocationRooms.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.MANUAL) {
            // Only the rooms travel: no host_id/host_name, because the backend
            // has no property to validate them against and its duplicate check
            // (one integration per bookable unit) does not apply either.
            editedIntegration = {
                uid: selectedPMSLocal.value,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else if (selectedPMSLocal.label === IntegrationCompany.ENSO_CONNECT) {
            if (!host_id && !host_name)
                return;

            editedIntegration = {
                uid: selectedPMSLocal.value,
                host_id: host_id,
                host_name: host_name,
                rooms: selectedLocationRoomsLocal.map(({value}) => value),
            };

        } else {
            return;
        }

        setRequestSave(true);
        disableError();

        // Both actions dispatch with rethrow: without this catch a rejected save
        // (a refused template, an unknown room) left the spinner turning forever
        // and said nothing -- the error died as an unhandled rejection.
        let created = false;
        try {
            if (isEditing) {
                // Editing a saved integration: same field extraction, but persist via
                // the update endpoint keyed on the integration's own uid (partial
                // update — the template uid in editedIntegration.uid is not sent).
                await dispatch(PMSLocationsActions.updateLocationIntegration({
                    integration_uid: integration.uid,
                    rooms: editedIntegration.rooms,
                    host_id: editedIntegration.host_id,
                    host_name: editedIntegration.host_name,
                    room_id: editedIntegration.room_id,
                    room_name: editedIntegration.room_name,
                }));
                setIsEditing(false);
            } else {
                await dispatch(PMSLocationsActions.addLocationIntegration(editedIntegration));
                created = true;
            }
        } catch (err: any) {
            setError(err?.message ?? String(err));
        } finally {
            setRequestSave(false);
        }

        // Last, and only on success: the server has it now, so this local row
        // must not outlive it -- and dropping it unmounts this component.
        if (created)
            onCreated?.();

    }, [
        dispatch,
        isEditing,
        integration.uid,
        onCreated,
        setError,
        disableError,
        selectedPMSLocal,
        selectedLocationRoomsLocal,
        selectedKrossBookingPropertyLocal,
        selectedKrossBookingRoomLocal,
        selectedHostifyPropertyLocal,
        selectedIcalLinkLocal.label,
        selectedOctorateAccomodationLocal.label,
        selectedOctorateAccomodationLocal.value,
        selectedSmoobuPropertyLocal,
        selectedAvantioAccommodationLocal,
        selectedLodgifyPropertyLocal,
        selectedGuestyListingLocal,
    ]);

    const canSave = useMemo(() => {
        return !isRequestingForSave && selectedPMSLocal !== undefined && (
            (
                selectedPMSLocal.label === IntegrationCompany.HOSTIFY &&
                selectedHostifyPropertyLocal !== undefined &&
                selectedHostifyPropertyLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.KROSS_BOOKING &&
                selectedKrossBookingPropertyLocal &&
                selectedKrossBookingRoomLocal !== undefined &&
                selectedKrossBookingRoomLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.SMOOBU &&
                selectedSmoobuPropertyLocal !== undefined &&
                selectedSmoobuPropertyLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.AVANTIO &&
                selectedAvantioAccommodationLocal !== undefined &&
                selectedAvantioAccommodationLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.OCTORATE &&
                selectedOctorateAccomodationLocal !== undefined &&
                selectedOctorateAccomodationLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.AVAIBOOK &&
                selectedIcalLinkLocal !== undefined &&
                selectedIcalLinkLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.LODGIFY &&
                selectedLodgifyPropertyLocal !== undefined &&
                selectedLodgifyPropertyLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.GUESTY &&
                selectedGuestyListingLocal !== undefined &&
                selectedGuestyListingLocal.value !== undefined
            ) ||
            (
                selectedPMSLocal.label === IntegrationCompany.ENSO_CONNECT &&
                host_name !== undefined &&
                host_id !== undefined
            ) ||
            // Nothing to pick but the rooms: there is no remote account, so no
            // property, listing or accommodation to point the integration at.
            selectedPMSLocal.label === IntegrationCompany.MANUAL
        ) && selectedLocationRoomsLocal.length > 0;
    }, [
        isRequestingForSave, 
        selectedPMSLocal, 
        selectedHostifyPropertyLocal, 
        selectedKrossBookingPropertyLocal, 
        selectedKrossBookingRoomLocal, 
        selectedSmoobuPropertyLocal,
        selectedLocationRoomsLocal,
        selectedIcalLinkLocal,
        selectedOctorateAccomodationLocal,
        selectedAvantioAccommodationLocal,
        selectedLodgifyPropertyLocal,
        selectedGuestyListingLocal,
        host_name,
        host_id
    ]);

    // Rooms currently assigned to this saved integration, derived from its
    // invitation groups. Reused by the init effect and to restore the selection
    // when an edit is cancelled.
    const computeSelectedRoomsFromGroups = useCallback(() => {
        let rooms: any[] = [];
        if (integration.groups?.length) {
            for (let i = 0; i < integration.groups.length; i++) {
                const integrationGroup = integration.groups[i];
                const group = groups.filter((g) => g.id === integrationGroup.id);
                if (group.length) {
                    // Concatenated, not assigned: an integration with more than
                    // one group kept only the last group's rooms, which the
                    // move then sends as the full assignment.
                    rooms = rooms.concat(group[0].invitation.rooms.map((room) => ({
                        value: room.uid,
                        label: room.name,
                        isDisabled: false,
                    })));
                }
            }
        }
        return rooms;
    }, [integration, groups]);

    // Enter edit mode on a saved integration: drop into the create form
    // pre-filled and load the PMS property/accommodation options so they can be
    // changed too. Resolved by templateUid, which says WHICH template the row
    // hangs from; the company match is the fallback for the rows that hang from
    // none, where any template of the company is as good a guess as before.
    // Skip the fetch when the stored credentials are known-invalid (a dead key
    // returns error 682); rooms stay editable regardless.
    const onStartEdit = useCallback(() => {
        setIsEditing(true);
        const template =
            integrationTemplates.find((t) => t.uid === integration.templateUid) ??
            integrationTemplates.find((t) => t.company === integration.company);
        if (template && integration.apikeyIsValid !== false) {
            onChangePMS({ value: template.uid, label: template.company, isDisabled: false });
        }
    }, [integrationTemplates, integration.templateUid, integration.company, integration.apikeyIsValid, onChangePMS]);

    const onCancelEdit = useCallback(() => {
        setSelectedLocationRoomsLocal(computeSelectedRoomsFromGroups());
        setIsEditing(false);
    }, [computeSelectedRoomsFromGroups]);

    // INITIALIZATION
    useEffect(() => {
        setAvailablePMS(buildAvailablePMS(integrationTemplates));
    }, [integrationTemplates, buildAvailablePMS]);

    useEffect(() => {
        if (integration.company === IntegrationCompany.KROSS_BOOKING) {
            setSelectedKrossBookingPropertyLocal(
                { 
                    value:integration.host_id, 
                    label:integration.host_name, 
                    isDisabled: false 
                }
            );
            setSelectedKrossBookingRoomLocal(
                { 
                    value:integration.room_id, 
                    label:integration.room_name, 
                    isDisabled: false 
                }
            );

        } else if (integration.company === IntegrationCompany.HOSTIFY) {
            setSelectedHostifyPropertyLocal(
                { 
                    value:integration.host_id, 
                    label:integration.host_name, 
                    isDisabled: false 
                }
            );

        } else if (integration.company === IntegrationCompany.SMOOBU) {
            setSelectedSmoobuPropertyLocal(
                { 
                    value:integration.host_id, 
                    label:integration.host_name, 
                    isDisabled: false 
                }
            );

        } else if (integration.company === IntegrationCompany.AVAIBOOK) {
            setSelectedIcalLinkLocal(
                {
                    value:integration.uid,
                    label:integration.link,
                    isDisabled: false
                }
            );

        } else if (integration.company === IntegrationCompany.AVANTIO) {
            setSelectedAvantioAccommodationLocal(
                {
                    value: integration.host_id,
                    label: integration.host_name,
                    isDisabled: false,
                }
            );

        } else if (integration.company === IntegrationCompany.LODGIFY) {
            setSelectedLodgifyPropertyLocal(
                { 
                    value:integration.host_id, 
                    label:integration.host_name, 
                    isDisabled: false 
                }
            );

        } else if (integration.company === IntegrationCompany.GUESTY) {
            setSelectedGuestyListingLocal(
                { 
                    value:integration.host_id, 
                    label:integration.host_name, 
                    isDisabled: false 
                }
            );

        }

        if (integration.groups?.length) {
            setSelectedLocationRoomsLocal(computeSelectedRoomsFromGroups());
        }
    }, [integration, groups, computeSelectedRoomsFromGroups]);


    if (!integration.uid && availablePMS.length === 0) {
        return (
            <div className="w-100 p-4 rounded-lg border text-center text-muted">
                {t("mod_pms_location_no_integration_templates")}
            </div>
        );
    }


    if (integration.uid && !isEditing)
        return integrationSummary();

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg border">
            <div
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "1fr 1fr",
                    }}
                    className="col-span-full d-flex justify-content-between"
                >
                <div className="c-min-w-40">
                    {availablePMS.length > 1 && !initialTemplate ? (
                        <Selector
                            hideSelectedOptions={false}
                            placeholder={t("mod_pms_select_pms_placeholder")}
                            value={selectedPMSLocal}
                            // `label` is the canonical company (it picks the
                            // platform branch), so react-select's search would
                            // only match that -- typing the name the option
                            // actually shows filtered everything out.
                            getOptionLabel={(option: any) =>
                                option.displayLabel ?? option.label
                            }
                            onChange={(selectedPMS: any) =>
                                onChangePMS(selectedPMS)
                            }
                            isDisabled={!integration.editable}
                            options={availablePMS}
                            formatOptionLabel={(option: any) => (
                                <div className="d-flex align-items-center">
                                    <img
                                        src={integrationLogoForCompany(option.label)}
                                        alt={option.label}
                                        className="mr-2 rounded"
                                        style={{ width: "40px", height: "40px", objectFit: "contain" }}
                                    />
                                    {option.displayLabel ?? option.label}
                                </div>
                            )}
                        />
                    ) : (
                        selectedPMSLocal && (
                            <div className="d-flex align-items-center">
                                <img
                                    src={integrationLogoForCompany(selectedPMSLocal.label)}
                                    alt={selectedPMSLocal.label}
                                    className="mr-2 rounded"
                                    style={{ width: "40px", height: "40px", objectFit: "contain" }}
                                />
                                {selectedPMSLocal.displayLabel ?? selectedPMSLocal.label}
                            </div>
                        )
                    )}
                </div>
            </div>

            {hostifyInput()}
            {krossBookingInput()}
            {smoobuInput()}
            {avantioInput()}
            {octorateInput()}
            {avaibookInput()}
            {lodgifyInput()}
            {guestyInput()}
            {threeAngular()}
            {icnea()}
            {bookipro()}
            {ensoConnectInput()}
            {manualInput()}

        </div>
    );

    // Which template governs this integration, and the action to change it. The
    // FK decides whose credentials and settings reach the row, so a manager who
    // attached it to the wrong account had no way to see it, let alone fix it
    // without deleting the integration (BAC-520).
    function onMoveTemplate() {
        dispatch(PMSLocationsActions.moveLocationIntegrationTemplateUi(
            integration.uid,
            integrationTemplates.filter(({ company }) => company === integration.company),
            integration.templateUid,
            integration.templateName,
            selectedLocationRoomsLocal.map(({ value }: any) => value),
        ));
    }

    function integrationSummary() {
        const roomsCount = selectedLocationRoomsLocal.length;
        const currentTemplate = integrationTemplates.find(
            ({ uid }) => uid === integration.templateUid,
        );
        // Somewhere to move it TO -- counting destinations and not the whole
        // list, because the current one may not even be listable here (a
        // colleague's personal account never is).
        const destinations = integrationTemplates.filter(
            ({ company, uid }) => company === integration.company
                && uid !== integration.templateUid,
        );
        // The rooms travel with the move and the backend takes them as the full
        // assignment, so a move made before the invitation groups resolve would
        // silently unassign them. Better no button for a moment than that.
        const canMoveTemplate = destinations.length > 0 && roomsCount > 0;
        // The account as the row knows it, for the buttons that open it: the
        // name travels along because the viewer's catalog may not list it.
        const templateRef = integration.templateUid
            ? { uid: integration.templateUid, name: integration.templateName }
            : null;
        // An account the catalog does not list is a colleague's personal one:
        // nobody but them can open it, so there is nothing to send the viewer
        // to -- the catalog would only show them an empty creation form.
        // Only once the list is in: before that, nothing is listed, and the
        // viewer's own accounts would read as somebody else's.
        const isForeignTemplate = templatesLoaded && !!integration.templateUid && !currentTemplate;
        const hasAccommodation =
            integration.host_name && integration.host_name !== "None";
        // Show the sync time only when a value arrives AND the company actually
        // pulls reservations. The developers/external-app integrations get a
        // last_reservations_checked stamped by the backend that doesn't reflect
        // a real sync, so they're filtered out explicitly.
        const lastSync =
            integration.lastReservationsChecked &&
            !COMPANIES_WITHOUT_RESERVATION_SYNC.includes(integration.company)
            ? TimeHelper.localizeIsoStringToFormat(
                  integration.lastReservationsChecked,
                  location.timezone,
                  TimeHelper.getTimeFormats().DATE_NO_SECONDS,
              )
            : null;
        return (
            <div className="c-grid c-grid--small w-100 c-hover-250 p-3 rounded-lg border">
                <div className="col-span-full d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center text-truncate mr-2">
                        <img
                            src={integrationLogoForCompany(integration.company)}
                            alt={integration.company}
                            className="mr-3 flex-shrink-0 rounded"
                            style={{ width: "56px", height: "56px", objectFit: "contain" }}
                        />
                        <div className="text-truncate">
                            <div className="text-secondary text-truncate">
                                {hasAccommodation &&
                                    `${t("mod_pms_location_integration_accommodation")}: ${integration.host_name}`}
                                {hasAccommodation && roomsCount > 0 && " · "}
                                {roomsCount > 0 &&
                                    `${t("mod_pms_location_integration_rooms")}: ${selectedLocationRoomsLocal.map((room: any) => room.label).join(", ")}`}
                            </div>
                            <div className="small text-secondary text-truncate">
                                {`${t("mod_pms_integration_template")}: `}
                                {integration.templateUid
                                    ? (integrationTemplateLabel(currentTemplate)
                                       || integration.templateName
                                       || integration.company)
                                    : <span className="text-warning">
                                          {t("mod_pms_integration_no_template")}
                                      </span>
                                }
                                {isForeignTemplate && (
                                    ` (${t("mod_pms_integration_template_foreign")})`
                                )}
                            </div>
                            {lastSync && (
                                <div className="small text-secondary text-truncate">
                                    {`${t("mod_pms_location_integration_last_sync")}: ${lastSync}`}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex flex-shrink-0">
                        {canMoveTemplate && (
                            <CapsuleButton
                                onClick={onMoveTemplate}
                                // "Asignar" and not "Cambiar" when it hangs from
                                // none: there is nothing to change yet.
                                text={t(integration.templateUid
                                    ? "mod_pms_integration_move_template_action"
                                    : "mod_pms_integration_assign_template_action")}
                                classNameExtra="mr-2"
                                // eslint-disable-next-line react/style-prop-object
                                style="INFO"
                                size="SMALL"
                            />
                        )}
                        {templatesLoaded && !integration.templateUid && destinations.length === 0 && (
                            // Hangs from none and there is none to assign: set
                            // one up, on this company, prefilled from the row's
                            // own (orphaned) credentials when it carries any.
                            <CapsuleButton
                                onClick={() => onBrowseAccount
                                    ? onBrowseAccount(integration.company, null, accountDraftOf(integration))
                                    : dispatch(
                                        PMSNavigationActions.goToIntegrationTemplate(
                                            integration.company, null, accountDraftOf(integration),
                                        ),
                                    )}
                                classNameExtra="mr-2 small"
                                // eslint-disable-next-line react/style-prop-object
                                style="INFO"
                                size="SMALL"
                                text={t("mod_pms_location_configure_integration_template")}
                            />
                        )}
                        {templatesLoaded && integration.templateUid && !isForeignTemplate && (
                            // The account itself (credentials, name) is edited
                            // on its template; open it where it lives.
                            <CapsuleButton
                                onClick={() => onBrowseAccount
                                    ? onBrowseAccount(integration.company, templateRef)
                                    : dispatch(
                                        PMSNavigationActions.goToIntegrationTemplate(integration.company, templateRef),
                                    )}
                                classNameExtra="mr-2 small"
                                // eslint-disable-next-line react/style-prop-object
                                style="INFO"
                                size="SMALL"
                                text={t("mod_pms_integration_go_to_template")}
                            />
                        )}
                        <CapsuleButton
                            onClick={onStartEdit}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="INFO"
                            size="SMALL"
                            icon={Icons.edit}
                        />
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    </div>
                </div>
                <IntegrationSyncStatus
                    integration={integration}
                    locationUid={location.uid}
                    onBrowseAccount={onBrowseAccount}
                    onAssignAccount={canMoveTemplate ? onMoveTemplate : undefined}
                    accountsLoaded={templatesLoaded}
                    isForeignAccount={isForeignTemplate}
                />
            </div>
        );
    }

    function hostifyInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.HOSTIFY) {
            return null;
        }

        return (
            <div>
               <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedHostifyPropertyLocal}
                        onChange={(selectedHostifyProperty: any) =>
                            onChangeHostifyProperty(selectedHostifyProperty)
                        }
                        isDisabled={!isEditable || availableHostifyProperties.length === 0}
                        options={availableHostifyProperties}
                        onMenuScrollToBottom={onLoadMoreHostifyProperties}
                        />
                </div>
                {footerInput()}
            </div>
        )
    }

    function krossBookingInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.KROSS_BOOKING) {
            return null;
        }

        return (
            <div>
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedKrossBookingPropertyLocal}
                        onChange={(selectedKrossBookingProperty: any) =>
                            onChangeKrossBookingProperty(selectedKrossBookingProperty)
                        }
                        isDisabled={availableKrossBookingProperties.length === 0}
                        options={availableKrossBookingProperties}
                        />
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_room")}
                        value={selectedKrossBookingRoomLocal}
                        onChange={(selectedRoom: any) =>
                            onChangeKrossBookingRoom(selectedRoom)
                        }
                        isDisabled={availableKrossBookingRooms.length === 0}
                        options={availableKrossBookingRooms}
                        />
                </div>
                
                {footerInput()}
            </div>
        )
    }

    function smoobuInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.SMOOBU) {
            return null;
        }

        return (
            <div>
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedSmoobuPropertyLocal}
                        onChange={(selectedProperty: any) =>
                            onChangeSmoobuProperty(selectedProperty)
                        }
                        isDisabled={availableSmoobuProperties.length === 0}
                        options={availableSmoobuProperties}
                        />
                </div>
                {footerInput()}
            </div>
        )
    }

    function avantioInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.AVANTIO) {
            return null;
        }

        return (
            <div>
               <div className="col-span-full">
                    <label className="font-weight-bold mb-1 d-flex align-items-center">
                        {t("mod_integrations_avantio_property_label")}
                        {isLoadingAvantioAccommodations && (
                            <span className="ml-2 font-weight-normal font-italic text-secondary">
                                {t("global_loading")}
                            </span>
                        )}
                    </label>
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={isLoadingAvantioAccommodations
                            ? t("global_loading")
                            : t("mod_integrations_select_property")}
                        value={selectedAvantioAccommodationLocal}
                        onChange={(selectedAccommodation: any) =>
                            onChangeAvantioAccommodation(selectedAccommodation)
                        }
                        isLoading={isLoadingAvantioAccommodations}
                        isDisabled={!isEditable || isLoadingAvantioAccommodations}
                        options={availableAvantioAccommodations}
                        />
                </div>
                {footerInput()}
            </div>
        )
    }

    function octorateInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.OCTORATE) {
            return null;
        }

        return (
            <div>
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedOctorateAccomodationLocal}
                        onChange={(selectedProperty: any) =>
                            onChangeOctorateAccomodation(selectedProperty)
                        }
                        isDisabled={availableOctorateAccomodations.length === 0}
                        options={availableOctorateAccomodations}
                        />
                </div>
                {footerInput()}
            </div>
        )
    }

    function avaibookInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.AVAIBOOK) {
            return null;
        }

        return (
            <div>
               <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_ical_url")}
                        value={selectedAvaibookAccommodationLocal}
                        onChange={(selectedAccomodation: any) =>
                            onChangeAvaibookAccommodations(selectedAccomodation)
                        }
                        isDisabled={!isEditable || availableAvaibookAccommodations.length === 0}
                        options={availableAvaibookAccommodations}
                        />
                </div>
                {footerInput()}
            </div>
        )
    }

    function lodgifyInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.LODGIFY) {
            return null;
        }

        return (
            <div>
               <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedLodgifyPropertyLocal}
                        onChange={(selectedLodgifyProperty: any) =>
                            onChangeLodgifyProperty(selectedLodgifyProperty)
                        }
                        isDisabled={!isEditable || availableLodgifyProperties.length === 0}
                        options={availableLodgifyProperties}
                        />
                </div>
                {footerInput()}
            </div>
        )
    }

    function threeAngular() {
        if (selectedPMSLocal?.label !== IntegrationCompany.THREE_ANGULAR) {
            return null;
        }

        return (
            <div>
               { integration.apikey  && 
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <div>TOKEN</div>
                    <input
                        className={`cna col-span-full w-100 p-2 c-h-min c-input-bordered`}
                        type="text"
                        readOnly={!integration.editable}
                        value={location.uid}
                        placeholder={undefined}
                        onChange={undefined}
                    />
                </div>
            }
               <div className="mt-2 d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                        {isEditing &&
                            <CapsuleButton
                                onClick={onCancelEdit}
                                classNameExtra="mr-2"
                                // eslint-disable-next-line react/style-prop-object
                                style="DANGER"
                                size="SMALL"
                                text={t("global_cancel")}
                            />
                        }
                        {(!integration.uid || isEditing) &&
                            <CapsuleButton
                                size="SMALL"
                                onClick={onSave}
                                text={t("global_save")}
                            />
                        }
                    </div>
                    {isRequestingForSave && <WaitSpinner />}
                </div>
                {errorMessage && (
                    <div className="text-danger mt-2">{errorMessage}</div>
                )}
            </div>
        )
    }

    function guestyInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.GUESTY) {
            return null;
        }

        return (
            <div>
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedGuestyListingLocal}
                        onChange={(selectedGuestyListing: any) =>
                            onChangeGuestyListing(selectedGuestyListing)
                        }
                        isDisabled={availableGuestyListings.length === 0}
                        options={availableGuestyListings}
                        />
                </div>
                
                {footerInput()}
            </div>
        )
    }

    function icnea() {
        if (selectedPMSLocal?.label !== IntegrationCompany.ICNEA) {
            return null;
        }

        return (
            <div>
               { integration.apikey  && 
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    {
                        <ToolTipWrapper
                            key={location.uid}
                            tooltipProps={getTooltipProps(location.uid)}
                            tooltip={getTooltip({name:"Token"})}
                        >
                            <div
                                key={location.uid}
                                onClick={() => handleCopyToClipboard(location.uid)}
                                className="c-hover-240 rounded-lg c-bg-grey-250"
                            >
                                <div
                                    className="rounded-lg px-3 p-2 "
                                >
                                    <h6 className="font-weight-bold m-0">{"Token"}</h6>
                                    {Uid(location.uid)}
                                </div>
                            </div>
                        </ToolTipWrapper>
                    }
                    
                </div>
                }
               <div className="mt-2 d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                        {isEditing &&
                            <CapsuleButton
                                onClick={onCancelEdit}
                                classNameExtra="mr-2"
                                // eslint-disable-next-line react/style-prop-object
                                style="DANGER"
                                size="SMALL"
                                text={t("global_cancel")}
                            />
                        }
                        {(!integration.uid || isEditing) &&
                            <CapsuleButton
                                size="SMALL"
                                onClick={onSave}
                                text={t("global_save")}
                            />
                        }
                    </div>
                    {isRequestingForSave && <WaitSpinner />}
                </div>
                {errorMessage && (
                    <div className="text-danger mt-2">{errorMessage}</div>
                )}
            </div>
        )
    }

    function bookipro() {
        if (selectedPMSLocal?.label !== IntegrationCompany.BOOKIPRO) {
            return null;
        }

        return (
            <div>
               { integration.apikey  &&
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    {
                        <ToolTipWrapper
                            key={location.uid}
                            tooltipProps={getTooltipProps(location.uid)}
                            tooltip={getTooltip({name:"Token"})}
                        >
                            <div
                                key={location.uid}
                                onClick={() => handleCopyToClipboard(location.uid)}
                                className="c-hover-240 rounded-lg c-bg-grey-250"
                            >
                                <div
                                    className="rounded-lg px-3 p-2 "
                                >
                                    <h6 className="font-weight-bold m-0">{"Token"}</h6>
                                    {Uid(location.uid)}
                                </div>
                            </div>
                        </ToolTipWrapper>
                    }

                </div>
                }
               <div className="mt-2 d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                        {isEditing &&
                            <CapsuleButton
                                onClick={onCancelEdit}
                                classNameExtra="mr-2"
                                // eslint-disable-next-line react/style-prop-object
                                style="DANGER"
                                size="SMALL"
                                text={t("global_cancel")}
                            />
                        }
                        {(!integration.uid || isEditing) &&
                            <CapsuleButton
                                size="SMALL"
                                onClick={onSave}
                                text={t("global_save")}
                            />
                        }
                    </div>
                    {isRequestingForSave && <WaitSpinner />}
                </div>
                {errorMessage && (
                    <div className="text-danger mt-2">{errorMessage}</div>
                )}
            </div>
        )
    }

    function ensoConnectInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.ENSO_CONNECT) {
            return null;
        }

        return (
            <div>
                <div
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        Listing Name
                    </div>
                    <ValidatedTextInput
                        isEditable={!integration.host_name || isEditing}

                        value={host_name}
                        setValue={setName}
                    />
                </div>
                <div className="mt-2" />
                <div
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        Listing ID
                    </div>
                    <ValidatedTextInput
                        isEditable={!integration.host_id || isEditing}

                        value={host_id}
                        setValue={setId}
                    />
                        
                </div>
                {footerInput()}
            </div>
        )
    }

    function manualInput() {
        if (selectedPMSLocal?.label !== IntegrationCompany.MANUAL) {
            return null;
        }

        // Rooms and nothing else: everything a PMS integration asks for here
        // describes the remote account, and Manual has none.
        return (
            <div>
                <p className="small text-muted mb-0">
                    {t("mod_pms_location_integration_manual_info")}
                </p>
                {footerInput()}
            </div>
        );
    }

    function handleCopyToClipboard(text) {
        navigator.clipboard.writeText(text);
    }

    function getTooltipProps(uid) {
        return {
            delayShow: 0,
            delayHide: 300,
            afterShow: () => handleCopyToClipboard(uid),
            event: "click",
            eventOff: "mouseout",
        };
    }

    function getTooltip({ name }) {
        return `${name}: ${t("global_uid_copied")}`
    }

    function Uid(uid) {
        return (
            <small className="font-italic text-secondary d-block">{uid}</small>
        );
    }

    function footerInput() {
        return (
            <div className="mt-4">
                <label className="font-weight-bold mb-1 d-flex align-items-center">
                    {t("mod_pms_location_integration_rooms")}
                </label>
                <div className="c-min-w-40">
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_room")}
                        isMulti
                        value={selectedLocationRoomsLocal}
                        onChange={(selectedRooms: any) =>
                            onChangeRoomsGroup(selectedRooms)
                        }
                        isDisabled={!isEditable}
                        options={availableLocationRooms}
                    />
                </div>
                <div className="mt-4 d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                        {isEditing &&
                            <CapsuleButton
                                onClick={onCancelEdit}
                                classNameExtra="mr-2"
                                // eslint-disable-next-line react/style-prop-object
                                style="DANGER"
                                size="SMALL"
                                text={t("global_cancel")}
                            />
                        }
                        {(!integration.uid || isEditing) &&
                            <CapsuleButton
                                size="SMALL"
                                isEnabled={canSave}
                                onClick={onSave}
                                text={t("global_save")}
                            />
                        }
                    </div>
                    {isRequestingForSave && <WaitSpinner />}
                </div>
                {errorMessage && (
                    <div className="text-danger mt-2">{errorMessage}</div>
                )}
            </div>
            
        )
    }

}
